import { valibotResolver } from "@hookform/resolvers/valibot";
import { PlusCircle } from "@phosphor-icons/react";
import clsx from "clsx";
import useAllLabels from "features/labels/datahooks/useAllLabels";
import useAllLabelsMutations from "features/labels/datahooks/useAllLabelsMutations";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import LabelColorSelection from "common/components/Labels/LabelColorSelection";
import LabelsList from "common/components/Labels/LabelsList";
import LabelTag from "common/components/LabelTag";
import { MenuSearchInput } from "common/components/MenuSearchInput";
import RenderIf from "common/components/RenderIf";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import { Tag } from "common/components/ui/Tag";
import { getSchemaMaxLength } from "common/helpers/utils";
import { LabelSchema, type LabelSchemaType } from "common/schemas";
import type { Label } from "common/types";

function sortLabels(allLabels: Label[], selectedLabels: Label[]) {
  return [
    ...[...selectedLabels].sort((label1, label2) =>
      label1.name.localeCompare(label2.name, undefined, {
        numeric: true,
      }),
    ),
    ...allLabels
      .filter(({ id }) => {
        const isSelected = !!selectedLabels.find(
          (existingLabel) => existingLabel.id === id,
        );
        return !isSelected;
      })
      .sort((label1, label2) =>
        label1.name.localeCompare(label2.name, undefined, {
          numeric: true,
        }),
      ),
  ];
}

const alignClassNames = {
  center: "justify-center",
  end: "justify-end",
  start: "justify-start",
} as const;

interface LabelsProps {
  labels: Label[];
  onRemoveLabel: (label: Label) => void;
  onAddLabel: (label: Label) => Promise<void>;
  align: "center" | "end" | "start";
  maxLabels?: number;
  size?: "sm" | "md";
}

function Labels({
  labels,
  onRemoveLabel,
  onAddLabel,
  align,
  size = "md",
  maxLabels = Infinity,
}: LabelsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const { handleSubmit, setValue, register, watch, reset } =
    useForm<LabelSchemaType>({
      defaultValues: {
        name: "",
      },
      resolver: valibotResolver(LabelSchema),
    });
  const { allLabels } = useAllLabels("workspace");
  const { createLabel } = useAllLabelsMutations("workspace");

  // we use a state here to prevent layout shifting, by updating it after the close animation is finished
  const [sortedLabels, setSortedLabels] = useState<Label[]>([]);

  useEffect(() => {
    if (allLabels) {
      setSortedLabels(sortLabels(allLabels ?? [], labels));
    }
  }, [allLabels]);

  // if labels or workspace labels change when closed, sort the list according to selection,
  // the delay ensures that it's sorted after the close animation
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setSortedLabels(sortLabels(allLabels ?? [], labels));
      }, 300);
    }
  }, [isOpen, labels, allLabels]);

  function toggleLeadLabel(label: Label, isSelected: boolean) {
    (isSelected ? onRemoveLabel : onAddLabel)(label);
  }

  function createAndAddLabel(label: LabelSchemaType) {
    createLabel(label).then((newLabel) => {
      onAddLabel(newLabel).then(() => {
        setIsCreatingNew(false);
        setTimeout(reset, 300);
      });
    });
  }

  const labelName = watch("name");
  const filteredLabels = sortedLabels.filter(({ name }) =>
    labelName ? name.toLowerCase().includes(labelName.toLowerCase()) : true,
  );

  const addLabelBtn = (
    <Tag
      variant="tertiary"
      size={size}
      asChild
      leftIcon={<PlusCircle weight="fill" />}
    >
      <button type="button" onClick={() => setIsOpen(true)}>
        Add new
      </button>
    </Tag>
  );

  return (
    <>
      <DropdownMenu
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsOpen(open);

            if (!isCreatingNew) {
              setTimeout(reset, 300);
            }
          }
        }}
      >
        <DropdownMenuTrigger asChild>
          <div
            className={clsx(
              "flex flex-wrap items-center gap-1.5",
              alignClassNames[align],
            )}
          >
            {labels.length ? (
              <>
                {labels.slice(0, maxLabels).map((label) => (
                  <LabelTag
                    key={label.id}
                    label={label}
                    size={size}
                    onClick={() => setIsOpen(true)}
                  />
                ))}

                {labels.length > maxLabels ? (
                  <Tag variant="quaternary" size={size} asChild>
                    <button type="button" onClick={() => setIsOpen(true)}>
                      +{labels.length - maxLabels} more
                    </button>
                  </Tag>
                ) : (
                  addLabelBtn
                )}
              </>
            ) : (
              addLabelBtn
            )}
          </div>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          className="z-aboveDialog min-w-80 max-w-[--radix-popper-anchor-width]"
          align={align}
        >
          <MenuSearchInput
            {...register("name")}
            placeholder={allLabels?.length ? "Search labels" : "Add new label"}
            maxLength={getSchemaMaxLength(LabelSchema.entries.name)}
          />

          <DropdownMenuList>
            <LabelsList
              toggleLabel={toggleLeadLabel}
              labels={filteredLabels}
              selectedLabels={labels}
              closePopup={() => setIsOpen(false)}
            />

            <RenderIf
              condition={
                labelName &&
                !filteredLabels.find(({ name }) => name === labelName)
              }
            >
              <DropdownMenuItem onSelect={() => setIsCreatingNew(true)}>
                <PlusCircle weight="fill" />
                Create a new Label ‘{labelName}’
              </DropdownMenuItem>
            </RenderIf>
          </DropdownMenuList>
        </DropdownMenuContent>
      </DropdownMenu>

      <LabelColorSelection
        labelName={labelName}
        isOpen={isCreatingNew}
        setIsOpen={setIsCreatingNew}
        onColorSelect={(color) => {
          setValue("color", color);
          handleSubmit(createAndAddLabel)();
        }}
      />
    </>
  );
}

Labels.displayName = "Labels";

export default Labels;
